/**
 * Derived-state maintenance shared by {@link ./commands} and {@link ./tick}: keep
 * generator multipliers in sync with active effects, and fire milestones whose
 * unlock condition has just become true. Pure — returns a new state plus events.
 */
import type { Content } from './content.js';
import { evalCondition, generatorMultiplier } from './selectors.js';
import { addToSet, type GameState } from './state.js';

/** Emitted when a milestone's condition is first met (render/ui juice — §2.2). */
export interface MilestoneEvent {
  readonly type: 'milestoneCompleted';
  readonly id: string;
}

/** Recompute every owned generator's cached production multiplier from effects. */
export function refreshMultipliers(state: GameState, content: Content): GameState {
  const generators: Record<string, GameState['generators'][string]> = {
    ...state.generators,
  };
  let changed = false;
  for (const [id, gen] of Object.entries(state.generators)) {
    const multiplier = generatorMultiplier(state, content, id);
    if (!multiplier.eq(gen.multiplier)) {
      generators[id] = { owned: gen.owned, multiplier };
      changed = true;
    }
  }
  return changed ? { ...state, generators } : state;
}

/**
 * Add every milestone whose condition now holds (fixpoint — a milestone's effect
 * can unlock the next), then refresh multipliers since milestone effects stack.
 */
export function checkMilestones(
  state: GameState,
  content: Content,
): { readonly state: GameState; readonly events: MilestoneEvent[] } {
  const events: MilestoneEvent[] = [];
  let current = state;
  let progressed = true;
  while (progressed) {
    progressed = false;
    for (const [id, milestone] of content.milestones) {
      if (current.milestones.includes(id)) continue;
      if (evalCondition(current, content, milestone.condition)) {
        current = { ...current, milestones: addToSet(current.milestones, id) };
        events.push({ type: 'milestoneCompleted', id });
        progressed = true;
      }
    }
  }
  if (events.length > 0) current = refreshMultipliers(current, content);
  return { state: current, events };
}
